import { Prec, useCodeMirror } from '@uiw/react-codemirror';
import React, { useEffect, useRef } from 'react';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { html as langHtml } from '@codemirror/lang-html';
import { minimalSetup } from '@uiw/codemirror-extensions-basic-setup';
import { standardKeymap } from '@codemirror/commands';
import { tags as t } from '@lezer/highlight';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import * as events from '@uiw/codemirror-extensions-events';
import {
  $createRangeSelection,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_BACKSPACE_COMMAND,
} from 'lexical';
import { mergeRegister } from '@lexical/utils';

import { takeJustInserted } from '../../nodes/html/justInsertedHtmlNodes';

// The highlight style can't see the app theme, so both palettes live in variables here.
const editorTheme = EditorView.theme({
  '&.cm-editor': {
    '--html-bg': '#fbf9f7',
    '--html-text': '#394047',
    '--html-gutter': '#CED4D9',
    '--html-cursor': 'black',
    '--html-keyword': '#5A5CAD',
    '--html-atom': '#6C8CD5',
    '--html-number': '#116644',
    '--html-variable': 'black',
    '--html-comment': '#0080FF',
    '--html-comment-bg': 'rgba(0,0,0,.05)',
    '--html-string': '#183691',
    '--html-tag': '#63a35c',
    '--html-attribute': '#795da3',
    background: 'var(--html-bg)',
  },
  '.dark &.cm-editor': {
    '--html-bg': 'var(--muted)',
    '--html-text': 'var(--foreground)',
    '--html-gutter': 'var(--muted-foreground)',
    '--html-cursor': 'var(--foreground)',
    '--html-keyword': '#ff7b72',
    '--html-atom': '#79c0ff',
    '--html-number': '#79c0ff',
    '--html-variable': 'var(--foreground)',
    '--html-comment': '#8b949e',
    '--html-comment-bg': 'rgba(255,255,255,.06)',
    '--html-string': '#a5d6ff',
    '--html-tag': '#7ee787',
    '--html-attribute': '#d2a8ff',
  },
  '&.cm-focused': {
    outline: '0',
  },
  '&.cm-editor .cm-content': {
    padding: '7px 0',
  },
  '&.cm-editor .cm-content, &.cm-editor .cm-gutter': {
    minHeight: '64px',
  },
  '&.cm-editor .cm-scroller': {
    overflow: 'auto',
  },
  '&.cm-editor .cm-gutters': {
    background: 'none',
    border: 'none',
    fontFamily: 'Consolas,Liberation Mono,Menlo,Courier,monospace;',
    color: 'var(--html-gutter)',
    fontSize: '0.8rem',
    lineHeight: '1.25rem',
  },
  '&.cm-editor .cm-lineNumbers': {
    padding: '0',
  },
  '&.cm-editor .cm-foldGutter': {
    width: '0',
  },
  '&.cm-editor .cm-line': {
    padding: '0 .8rem',
    color: 'var(--html-text)',
    fontFamily: 'Consolas,Liberation Mono,Menlo,Courier,monospace;',
    fontSize: '0.8rem',
    lineHeight: '1.25rem',
  },
  '&.cm-editor .cm-activeLine, &.cm-editor .cm-activeLineGutter': {
    background: 'none',
  },
  '&.cm-editor .cm-cursor, &.cm-editor .cm-dropCursor': {
    borderLeft: '1.2px solid var(--html-cursor)',
  },
});

const editorHighlightStyle = HighlightStyle.define([
  { tag: t.keyword, color: 'var(--html-keyword)' },
  { tag: t.atom, color: 'var(--html-atom)' },
  { tag: t.number, color: 'var(--html-number)' },
  { tag: t.definition(t.variableName), textDecoration: 'underline' },
  { tag: t.variableName, color: 'var(--html-variable)' },
  {
    tag: t.comment,
    color: 'var(--html-comment)',
    fontStyle: 'italic',
    background: 'var(--html-comment-bg)',
  },
  { tag: [t.string, t.special(t.brace)], color: 'var(--html-string)' },
  { tag: t.meta, color: 'yellow' },
  { tag: t.bracket, color: 'var(--html-tag)' },
  { tag: t.tagName, color: 'var(--html-tag)' },
  { tag: t.attributeName, color: 'var(--html-attribute)' },
]);

export default function HtmlEditor({
  html,
  nodeKey,
  updateHtml,
  escapeUp,
  escapeDown,
  removeNode,
}) {
  const [lexicalEditor] = useLexicalComposerContext();

  // Read once per mounted block, not per render: the key is consumed on the
  // first read, and a re-render must not turn a just-inserted block into a
  // restored one.
  const autoFocus = useRef(null);
  if (autoFocus.current === null) autoFocus.current = takeJustInserted(nodeKey);

  const editor = useRef();

  const onChange = React.useCallback(
    value => {
      updateHtml(value);
    },
    [updateHtml],
  );

  // Base extensions for the CodeMirror editor
  const extensions = [
    editorTheme,
    syntaxHighlighting(editorHighlightStyle),
    EditorView.lineWrapping, // wraps lines that exceed the viewport width
    lineNumbers(), // adds line numbers to the gutter
    minimalSetup({ defaultKeymap: false }), // disable defaultKeymap to prevent Mod+Enter from inserting new line
    // Escape the editor with ArrowUp/ArrowDown from the first/last line.
    // Prec.highest because CodeMirror registers keymaps above plain handlers,
    // and standardKeymap swallows boundary arrows (first-line ArrowUp moves to
    // the line start). stopPropagation keeps Lexical from also handling the key
    Prec.highest(
      EditorView.domEventHandlers({
        keydown: (event, editorView) => {
          if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) {
            return false;
          }

          // if the editor has no content, remove the node from the lexical
          // editor; consume the event fully so lexical doesn't also delete a
          // character at the selection removeNode leaves behind
          if (event.key === 'Backspace') {
            if (editorView.state.doc.length !== 0) return false;

            event.preventDefault();
            event.stopPropagation();
            removeNode();

            return true;
          }

          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
            return false;
          }

          const { state } = editorView;
          const cursorLine = state.doc.lineAt(state.selection.main.head).number;
          const atBoundary =
            event.key === 'ArrowUp'
              ? cursorLine === 1
              : cursorLine === state.doc.lines;

          if (!atBoundary) return false;

          event.preventDefault();
          event.stopPropagation();

          // blur first: the blur handler below resets the lexical selection,
          // so the escape callback must set the real selection after it
          editorView.contentDOM.blur();

          if (event.key === 'ArrowUp') {
            escapeUp();
          } else {
            escapeDown();
          }

          return true;
        },
      }),
    ),
    keymap.of(closeBracketsKeymap), // required for quote completion; needs to be listed before standardKeymap
    keymap.of(standardKeymap), // add back in standardKeymap, which doesn't include Mod+Enter
    keymap.of([
      {
        key: 'Mod-z',
        stopPropagation: true,
      },
      {
        key: 'Mod-y',
        stopPropagation: true,
      },
    ]),
    langHtml(),
    closeBrackets(),
    events.content({
      // remove selection from lexical editor when CodeMirror editor is focused
      focus: () => {
        lexicalEditor.update(() => {
          $setSelection(null);
        });
      },
      blur: () => {
        lexicalEditor.update(() => {
          // keep a selection already placed by the escape/remove handlers
          if ($getSelection() !== null) return;

          // Set a range selection
          const rangeSelection = $createRangeSelection();
          $setSelection(rangeSelection);
        });
      },
    }),
  ];

  const { setContainer, view } = useCodeMirror({
    value: html,
    container: editor.current,
    extensions: extensions,
    autoFocus: autoFocus.current,
    basicSetup: false,
    onChange: onChange,
  });

  // The decorator wrapper is contenteditable=false, so the native caret can't
  // move from an adjacent paragraph into the CodeMirror editor; focus it here
  useEffect(() => {
    if (!view) return undefined;

    const enterEditor = (event, sibling) => {
      // arrows inside the CodeMirror editor are handled by its own keydown
      if (view.hasFocus) return false;

      const selection = $getSelection();

      if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
        return false;
      }

      const htmlNode = $getNodeByKey(nodeKey);
      const topLevelElement = selection.anchor.getNode().getTopLevelElement();

      if (!htmlNode || topLevelElement === null) return false;

      if (
        sibling === 'previous'
          ? topLevelElement.getPreviousSibling() !== htmlNode
          : topLevelElement.getNextSibling() !== htmlNode
      ) {
        return false;
      }

      event.preventDefault();
      view.focus();
      view.dispatch({
        selection: {
          anchor: sibling === 'previous' ? view.state.doc.length : 0,
        },
      });

      return true;
    };

    // Backspace at the start of the paragraph below the block would select the
    // block invisibly (no selected styling) and delete it on the next press.
    // Enter the editor instead; the block is deleted from inside when empty
    const onBackspace = event => {
      if (view.hasFocus) return false;

      const selection = $getSelection();

      if (
        !$isRangeSelection(selection) ||
        !selection.isCollapsed() ||
        selection.anchor.offset !== 0
      ) {
        return false;
      }

      const htmlNode = $getNodeByKey(nodeKey);
      const anchorNode = selection.anchor.getNode();
      const topLevelElement = anchorNode.getTopLevelElement();

      if (!htmlNode || topLevelElement === null) return false;
      if (topLevelElement.getPreviousSibling() !== htmlNode) return false;

      // offset 0 only means the paragraph start when the anchor is the
      // paragraph itself or its first descendant
      if (
        anchorNode !== topLevelElement &&
        anchorNode !== topLevelElement.getFirstDescendant()
      ) {
        return false;
      }

      if (topLevelElement.getTextContent() === '') {
        topLevelElement.remove();
      }

      event.preventDefault();
      view.focus();
      view.dispatch({ selection: { anchor: view.state.doc.length } });

      return true;
    };

    return mergeRegister(
      lexicalEditor.registerCommand(
        KEY_ARROW_UP_COMMAND,
        event => enterEditor(event, 'previous'),
        COMMAND_PRIORITY_LOW,
      ),
      lexicalEditor.registerCommand(
        KEY_ARROW_DOWN_COMMAND,
        event => enterEditor(event, 'next'),
        COMMAND_PRIORITY_LOW,
      ),
      lexicalEditor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        onBackspace,
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [lexicalEditor, view, nodeKey]);

  useEffect(() => {
    if (editor.current) {
      setContainer(editor.current);
    }
  }, [editor.current]);

  return (
    <div className="my-4">
      <div ref={editor} />
    </div>
  );
}
